import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

// Lightweight WYSIWYG for proposal long-text fields (Nature of Work, Description,
// Deliverables). Wraps Quill v2 directly (framework-agnostic — avoids react-quill peer
// issues on React 19). Pasting from Word keeps bold/italic/underline, bullet & numbered
// lists, and headings; Quill's clipboard strips the Word cruft. onChange emits
// getSemanticHTML() — clean <ul>/<ol>/<h2>/<strong>/<u> that renders correctly anywhere
// (e.g. the client portal) WITHOUT needing Quill's stylesheet.

const TOOLBAR = [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
];

export default function RichTextEditor({ value, onChange, minHeight = 150, placeholder }) {
    const elRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const quill = new Quill(elRef.current, {
            theme: "snow",
            placeholder: placeholder || "",
            modules: { toolbar: TOOLBAR },
        });
        quillRef.current = quill;

        // Seed initial content (stored semantic HTML, or legacy plain text).
        if (value) {
            quill.clipboard.dangerouslyPasteHTML(value);
            quill.setSelection(quill.getLength(), 0);
        }

        quill.on("text-change", () => {
            const html = quill.getSemanticHTML().trim();
            // Quill renders an empty editor as "<p></p>"/"<p><br></p>"; normalize to "".
            onChangeRef.current(/^<p>(<br>)?<\/p>$/i.test(html) || html === "" ? "" : html);
        });
        // Init once; the parent remounts this component (via `key`) when the proposal
        // changes, so there's no need to diff `value` on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={{ background: "#fff", borderRadius: 6 }}>
            <div ref={elRef} style={{ minHeight }} />
        </div>
    );
}
